import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  MessageSquare,
  Phone,
  StickyNote,
  Voicemail,
  MessagesSquare,
  Camera,
  Activity,
  ArrowUpRight,
} from "lucide-react";
import {
  Item,
  ItemContent,
  ItemMedia,
  ItemTitle,
  ItemDescription,
  ItemActions,
} from "@/components/ui/item";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn, getInitials } from "@/lib/utils";
import type { EntityActivityRow } from "@adva/types";
import { AttachmentStrip } from "./attachment-strip";

type IconType = React.ComponentType<{ className?: string }>;

// Comm channel → Lucide glyph (ADR-0019 Amendment A). String-keyed so the
// nullable EntityActivityRow.channel indexes without a cast; unknown → fallback.
const CHANNEL_ICONS: Record<string, IconType> = {
  sms: MessageSquare,
  email: Mail,
  call: Phone,
  chat: MessagesSquare,
  note: StickyNote,
  voicemail: Voicemail,
};

// The type glyph answers "what kind of event" — channel for comms, a camera for
// job-site media, a generic activity marker for lifecycle events. Per imagery
// §I2.1a (ADV-1344) this glyph no longer occupies the identity slot (the avatar
// answers "who"); it renders as a small muted source badge in the title line so
// comm-vs-media-vs-activity stays distinguishable.
function iconForRow(row: EntityActivityRow): IconType {
  if (row.source === "media") return Camera;
  if (row.source === "activity") return Activity;
  return (row.channel && CHANNEL_ICONS[row.channel]) || MessageSquare;
}

// Per imagery §I7.1 fallback chain: image → monogram → entity-type icon. When
// no actor resolves (system / import-lifecycle / unattributed rows), the
// fallback is the entity-type glyph on a surface circle, NOT a person monogram
// (§I2.1a rule 4 — a monogram implies a human actor; absence of one is honest).
function FallbackContent({ row }: { row: EntityActivityRow }) {
  const initials = getInitials(row.actor_name);
  if (initials) {
    return <>{initials}</>;
  }
  const Icon = iconForRow(row);
  return <Icon className="size-4" aria-hidden />;
}

// Route-backed source-link targets, keyed by the singular source_entity_type
// the feed emits (matches core.log_activity_event CASE arms +
// ops.media_subjects entity_type CHECK + SOURCE_LINK_PRIORITY in
// entity-activity.ts). Every prefix here has a registered `:id` detail route in
// App.tsx. Entity types WITHOUT a detail route (e.g. service_plan — only a
// service-plans index, no `:id` page) are deliberately absent, so those rows
// render plain text instead of a dead link (follow-ups-section.tsx documents
// the same missing-route guard).
const SOURCE_ROUTE_PREFIX: Record<string, string> = {
  opportunity: "/opportunities",
  job: "/jobs",
  proposal: "/proposals",
  invoice: "/invoices",
  customer: "/customers",
  appointment: "/appointments",
  warranty: "/warranties",
  warranty_claim: "/warranty-claims",
  service_agreement: "/service-agreements",
};

function sourceHref(row: EntityActivityRow): string | null {
  const type = row.source_entity_type;
  const id = row.source_entity_id;
  if (!type || !id) return null;
  const prefix = SOURCE_ROUTE_PREFIX[type];
  return prefix ? `${prefix}/${id}` : null;
}

// One label per route-backed source type for the "View …" affordance. Keys
// mirror SOURCE_ROUTE_PREFIX exactly — a type with a route but no label (or vice
// versa) would render a half-link, so the two maps must stay in lockstep.
const SOURCE_LABEL: Record<string, string> = {
  opportunity: "opportunity",
  job: "job",
  proposal: "proposal",
  invoice: "invoice",
  customer: "customer",
  appointment: "appointment",
  warranty: "warranty",
  warranty_claim: "warranty claim",
  service_agreement: "service agreement",
};

// §4.3 feed exception (ADV-1344): the activity feed shows the ABSOLUTE date +
// time inline, with the relative form ("3 days ago") in the hover tooltip — the
// inverse of the §4.3 default. On a chronological timeline the operator's
// question is "exactly when," and an absolute timestamp is the unambiguous,
// searchable answer. `display` is the absolute form; `full` is the relative
// tooltip.
function formatTimestamp(iso: string): { display: string; full: string } {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  const diffHr = Math.round(diffMs / 3_600_000);
  const diffDay = Math.round(diffMs / 86_400_000);

  let relative: string;
  if (diffMin < 1) relative = "just now";
  else if (diffMin < 60) relative = `${diffMin}m ago`;
  else if (diffHr < 24) relative = `${diffHr}h ago`;
  else if (diffDay < 30) relative = `${diffDay}d ago`;
  else relative = "over a month ago";

  // Absolute date + time. Omit the year when the event is in the current
  // calendar year (it is implied) to keep the column tight; keep it otherwise.
  const includeYear = date.getFullYear() !== now.getFullYear();
  const display = date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
    hour: "numeric",
    minute: "2-digit",
  });
  return { display, full: relative };
}

interface ActivityRowProps {
  // A normalized row from the three-source merged feed (comms + media + activity
  // events). The component dispatches on `row.source` so media photos and
  // activity events render alongside communications instead of being dropped.
  row: EntityActivityRow;
  // Right-rail preview mode trims the row: hides body + attachments, omits the
  // direction accent border, smaller padding, and shrinks the actor avatar to
  // --avatar-sm (24px) per §A3.1.
  compact?: boolean;
  // ADV-1345: a stable, parseable timestamp the lifecycle scroll-spy addresses
  // rows by. Emitted as `data-occurred-at` on the row's root DOM node so the
  // hook can find "the first row at/after a milestone" without coupling to the
  // row's id. Optional — the feed sets it; the Overview preview omits it.
  "data-occurred-at"?: string;
}

export function ActivityRow({
  row,
  compact = false,
  "data-occurred-at": dataOccurredAt,
}: ActivityRowProps) {
  const Glyph = iconForRow(row);
  const ts = useMemo(() => formatTimestamp(row.occurred_at), [row.occurred_at]);

  const attachments = row.attachments ?? [];
  const isMedia = row.source === "media";
  // Outbound comms are the only direction-bearing rows; keep the 2px accent as
  // the single non-icon direction signal (no colored fills — ADR-0019 §6 #6).
  const isOutbound =
    row.source === "communication" && row.direction === "outbound";
  const isInternal =
    row.source === "communication" && row.direction === "internal";

  // Surface tokens map to register: outbound = surface (one step lifted),
  // internal = elevated, everything else = bg (recessed). No bubble fills.
  const surface = isOutbound
    ? "bg-[color:var(--color-surface)]"
    : isInternal
      ? "bg-[color:var(--color-elevated)]"
      : "bg-[color:var(--color-bg)]";

  const accent =
    !compact && isOutbound
      ? "border-l-2 border-l-[color:var(--color-accent)]"
      : "";

  // --avatar-md (32px) full-density, --avatar-sm (24px) compact rail per §A3.1.
  const avatarSize = compact ? "size-6" : "size-8";

  const href = sourceHref(row);
  const sourceLabel = row.source_entity_type
    ? SOURCE_LABEL[row.source_entity_type]
    : undefined;

  return (
    <Item
      variant="outline"
      size={compact ? "sm" : "default"}
      data-occurred-at={dataOccurredAt}
      className={cn(
        "border-[color:var(--color-border)]",
        surface,
        accent,
        "items-start",
      )}
    >
      <ItemMedia className="self-start">
        {/* §I2.1a: person-avatar — headshot preferred, monogram fallback, then
            entity-type glyph for system/unattributed rows. Radix AvatarImage
            auto-falls-back to AvatarFallback on a missing/failed src, so no
            manual onError is needed (§I7.2 — no broken-image, ever). */}
        <Avatar className={avatarSize}>
          {row.actor_avatar_url && (
            <AvatarImage
              src={row.actor_avatar_url}
              alt={row.actor_name ?? ""}
            />
          )}
          <AvatarFallback className="bg-[color:var(--color-surface)] text-[color:var(--color-text-md)] typo-mono text-xs">
            <FallbackContent row={row} />
          </AvatarFallback>
        </Avatar>
      </ItemMedia>
      <ItemContent>
        <ItemTitle className="flex items-center gap-2 text-[color:var(--color-text-hi)]">
          {/* Relocated type glyph (§I2.1a rule 3) — the "what kind of event"
              signal, muted, no longer in the identity slot. */}
          <Glyph
            className="size-3.5 shrink-0 text-[color:var(--color-text-lo)]"
            aria-hidden
          />
          {row.actor_name && (
            <span className="typo-body font-medium text-[color:var(--color-text-hi)]">
              {row.actor_name}
            </span>
          )}
          {row.actor_name && (
            <span className="text-[color:var(--color-text-lo)]" aria-hidden>
              ·
            </span>
          )}
          <span className="typo-body text-[color:var(--color-text-md)]">
            {row.label}
          </span>
        </ItemTitle>
        {!compact && row.body && (
          <ItemDescription className="typo-body text-[color:var(--color-text-md)] whitespace-pre-wrap">
            {row.body}
          </ItemDescription>
        )}
        {compact && row.body && (
          <ItemDescription className="typo-body text-[color:var(--color-text-md)] line-clamp-1">
            {row.body}
          </ItemDescription>
        )}
        {!compact && attachments.length > 0 && (
          <AttachmentStrip
            attachments={attachments}
            size={isMedia ? "md" : "sm"}
          />
        )}
      </ItemContent>
      <ItemActions
        className="flex-col items-end gap-1 self-start"
        title={ts.full}
      >
        {/* §4.3 feed exception: absolute date + time inline; relative in the
            ItemActions tooltip above. The machine-readable ISO stays on the
            <time> element. */}
        <time
          dateTime={row.occurred_at}
          className="typo-mono text-xs tabular-nums text-[color:var(--color-text-lo)]"
        >
          {ts.display}
        </time>
        {/* Source link — a single real anchor (no nested-interactive a11y
            violation; the whole Item is inert). Renders only for route-backed
            source types; warranty / customer / appointment rows show no link. */}
        {!compact && href && sourceLabel && (
          <Link
            to={href}
            className="inline-flex items-center gap-0.5 typo-mono text-xs text-[color:var(--color-text-md)] underline-offset-4 hover:text-[color:var(--color-accent)] hover:underline"
          >
            View {sourceLabel}
            <ArrowUpRight className="size-3" aria-hidden />
          </Link>
        )}
      </ItemActions>
    </Item>
  );
}
